import { FACTIONS, GALAXY, HOME, NODES, distance, within } from "./data";
import { clone, random, seedHash, shuffled } from "./rng";
import type { FactionId, GameState, GalaxyMap, GalaxyNode } from "./types";

const indices = new WeakMap<GalaxyMap, Record<string, GalaxyNode>>();
export const galaxyOf = (g?: Pick<GameState, "galaxy"> | null) =>
  g?.galaxy ?? GALAXY;
export function nodesOf(
  g?: Pick<GameState, "galaxy"> | null,
): Record<string, GalaxyNode> {
  if (!g?.galaxy) return NODES;
  let nodes = indices.get(g.galaxy);
  if (!nodes) {
    nodes = Object.fromEntries(g.galaxy.nodes.map((n) => [n.id, n]));
    indices.set(g.galaxy, nodes);
  }
  return nodes;
}
export const routeDistance = (g: GameState, a: string, b: string, max = 36) =>
  distance(a, b, max, nodesOf(g));
export const scanRange = (g: GameState, id: string, radius: number) =>
  within(id, radius, nodesOf(g));

/** Isolated seeded generator: layout never consumes the combat/campaign RNG. */
export function generateGalaxy(seed: string, active: FactionId[]): GalaxyMap {
  const rng = { rng: seedHash("meridian-v2:" + seed) };
  const nodes: GalaxyNode[] = clone(GALAXY.nodes);
  const by = Object.fromEntries(nodes.map((n) => [n.id, n]));
  for (const n of nodes) n.neighbors = [];
  const connect = (a: GalaxyNode, b: GalaxyNode) => {
    if (!a.neighbors.includes(b.id)) a.neighbors.push(b.id);
    if (!b.neighbors.includes(a.id)) b.neighbors.push(a.id);
  };
  const patterns: Record<number, number[]> = {
    2: [0, 3],
    3: [0, 2, 4],
    4: [0, 1, 3, 4],
    5: [0, 1, 2, 3, 4],
    6: [0, 1, 2, 3, 4, 5],
  };
  const offset = Math.floor(random(rng) * 6),
    mirror = random(rng) < 0.5 ? 1 : -1;
  const rotate = (seat: number) => (offset + mirror * seat + 12) % 6;
  const assigned = new Map<FactionId, number>();
  const activeOrder = [active[0], ...shuffled(active.slice(1), rng)];
  activeOrder.forEach((f, i) =>
    assigned.set(f, rotate(patterns[active.length][i])),
  );
  const free = shuffled(
    [0, 1, 2, 3, 4, 5].filter((s) => ![...assigned.values()].includes(s)),
    rng,
  );
  for (const f of shuffled(
    FACTIONS.filter((f) => !assigned.has(f)),
    rng,
  ))
    assigned.set(f, free.pop()!);
  const rotation = random(rng) * Math.PI * 2;
  const jitter = (size: number) => (random(rng) * 2 - 1) * size;
  const place = (
    n: GalaxyNode,
    seat: number,
    radius: number,
    angleOffset = 0,
  ) => {
    const angle = rotation + (seat * Math.PI) / 3 + angleOffset + jitter(0.025);
    const r = radius + jitter(0.012);
    n.x = Math.round(500 + Math.cos(angle) * 420 * r);
    n.y = Math.round(380 + Math.sin(angle) * 300 * r);
  };
  const centers = shuffled(
    nodes.filter((n) => n.sector === "core"),
    rng,
  );
  centers.forEach((n, i) => {
    place(n, i, 0.29);
    if (!n.special) n.stars = random(rng) < 0.5 ? 2 : 3;
  });
  centers.forEach((n, i) => connect(n, centers[(i + 1) % 6]));
  for (const f of FACTIONS) {
    const seat = assigned.get(f)!;
    const home = by[HOME[f]];
    const [a, b, c, d] = shuffled(
      nodes.filter((n) => n.sector === f && n.id !== home.id),
      rng,
    );
    place(home, seat, 1);
    place(a, seat, 0.79, -0.14);
    place(b, seat, 0.79, 0.14);
    place(c, seat, 0.62, jitter(0.035));
    place(d, seat, 0.47, jitter(0.04));
    const near = shuffled([1, 2], rng);
    [a, b, c, d].forEach((n, i) => {
      n.stars = [near[0], near[1], 2, 3][i];
      n.visualVariant = [
        "temperate",
        "ice",
        "rock",
        "gas",
        "ocean",
        "volcanic",
        "dark",
        "desert",
      ][Math.floor(random(rng) * 8)];
    });
    connect(home, a);
    connect(home, b);
    connect(a, c);
    connect(b, c);
    connect(c, d);
    connect(d, centers[seat]);
    connect(d, centers[(seat + (random(rng) < 0.5 ? 1 : 5)) % 6]);
    if (random(rng) < 0.55) connect(a, b);
  }
  for (let i = 0; i < 2; i++) {
    const a = Math.floor(random(rng) * 6);
    connect(centers[a], centers[(a + 2 + Math.floor(random(rng) * 2)) % 6]);
  }
  for (const n of nodes) n.neighbors.sort();
  return { generator: "meridian-v2", nodes };
}
